'use client';
import { useTheme } from '@mui/material';
import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { useAssetFinanceCalculators } from '@/lib/hooks/useAssetFinanceCalculators';
import {
  ComponentBlocksLoanCalculators,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types.d';

import {
  CalculatorInputs,
  CalculatorLayout,
  CalculatorOutputs,
  OutputComparison,
  OutputField,
  OutputGraphicStackedBar,
  StyledTextSlider,
  StyledToggleButtonField,
} from './components';

type GuarantorLoanFormValues = {
  loanAmount: number;
  loanTermYears: number;
  repaymentFrequency: 'monthly' | 'fortnightly' | 'weekly';
  securedRate: number;
  unsecuredRate: number;
  guarantorPropertyValue: number;
  existingMortgage: number;
};
const defaultValues = {
  loanAmount: 500000,
  loanTermYears: 30,
  repaymentFrequency: 'monthly' as 'monthly' | 'fortnightly' | 'weekly' | undefined,
  securedRate: 6.5,
  unsecuredRate: 8.5,
  guarantorPropertyValue: 800000,
  existingMortgage: 200000,
};

const GuarantorLoan = ({ block }: { block: ComponentBlocksLoanCalculators }) => {
  const theme = useTheme();
  const { calculateGuarantorLoan } = useAssetFinanceCalculators();
  const methods = useForm<GuarantorLoanFormValues>({
    defaultValues,
  });
  const { control, watch } = methods;
  const values = watch();

  const result = calculateGuarantorLoan({
    loanAmount: values.loanAmount || 0,
    loanTermYears: values.loanTermYears || 0,
    securedRate: values.securedRate || 0,
    unsecuredRate: values.unsecuredRate || 0,
    repaymentFrequency: values.repaymentFrequency || 'monthly',
    guarantorPropertyValue: values.guarantorPropertyValue || 0,
    existingMortgage: values.existingMortgage || 0,
  });

  return (
    <FormProvider {...methods}>
      <CalculatorLayout
        title={block?.CustomCalculatorTitle ?? undefined}
        blurb={block?.CalculatorBlurb}
        backgroundOptions={(block?.CalculatorBackground ?? undefined) as StrapiBackgroundOptions}
        displayOptions={
          {
            ...block?.CalculatorDisplayOptions,
            ContentsWidth:
              block?.CalculatorDisplayOptions?.ContentsWidth ??
              Enum_Componentshareddisplayoptions_Contentswidth.Wide,
            PaddingTop: block?.CalculatorDisplayOptions?.PaddingTop ?? 'md',
            PaddingBottom: block?.CalculatorDisplayOptions?.PaddingBottom ?? 'md',
            PaddingInline: block?.CalculatorDisplayOptions?.PaddingInline ?? 'sm',
          } as StrapiDisplayOptions
        }
        rowLayout={false}
        dataComponent="GuarantorLoan"
      >
        <CalculatorInputs title={block?.CustomInputsTitle ?? 'Select loan terms'}>
          <Controller
            name="loanAmount"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Loan Amount"
                prefix="$"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="loanTermYears"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Loan Term"
                suffix={field.value === 1 ? ' year' : ' years'}
                value={field.value}
                minValue={1}
                maxValue={30}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="securedRate"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Secured Rate (with guarantor)"
                suffix="%"
                value={field.value}
                defaultMax={30}
                step={0.1}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="unsecuredRate"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Unsecured Rate (without guarantor)"
                suffix="%"
                value={field.value}
                defaultMax={30}
                step={0.1}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="guarantorPropertyValue"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Guarantor Property Value"
                prefix="$"
                value={field.value}
                defaultMax={defaultValues.guarantorPropertyValue}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="existingMortgage"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Existing Mortgage on Guarantor Property"
                prefix="$"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="repaymentFrequency"
            control={control}
            render={({ field }) => (
              <StyledToggleButtonField
                label="Repayment Frequency"
                value={field.value}
                onChange={(value) =>
                  field.onChange(value as GuarantorLoanFormValues['repaymentFrequency'])
                }
                options={[
                  { value: 'monthly', label: 'Monthly' },
                  { value: 'fortnightly', label: 'Fortnightly' },
                  { value: 'weekly', label: 'Weekly' },
                ]}
                sx={{
                  '& .MuiToggleButtonGroup-root': {
                    display: { xs: 'flex', md: 'row' },
                    flexDirection: 'row',
                  },
                }}
              />
            )}
          />
        </CalculatorInputs>

        <CalculatorOutputs title={block?.CustomOutputsTitle ?? 'Guarantor Loan Summary'}>
          <OutputGraphicStackedBar
            sx={{
              mx: 'auto',
            }}
            categories={['Unsecured', 'Secured']}
            series={[
              {
                label: 'Principal',
                data: [values.loanAmount || 0, values.loanAmount || 0],
                color: theme.palette.brand.primary,
              },
              {
                label: 'Interest',
                data: [result.unsecuredTotalInterestRaw, result.securedTotalInterestRaw],
                color: theme.palette.brand.secondary,
              },
            ]}
          />
          <OutputComparison
            columnTitles={[
              { title: 'Unsecured', subtitle: `${values.unsecuredRate}%` },
              { title: 'Secured', subtitle: `${values.securedRate}%` },
            ]}
            highlightedColumn={1}
            rows={[
              {
                label: `Payment (${result.paymentLabel})`,
                values: [`$${result.unsecuredPayment}`, `$${result.securedPayment}`],
                deltas: [null, `-$${result.repaymentSaving}`],
              },
              {
                label: 'Total Interest',
                values: [`$${result.unsecuredTotalInterest}`, `$${result.securedTotalInterest}`],
                deltas: [null, `-$${result.totalInterestSaving}`],
              },
              {
                label: 'Total Repaid',
                values: [
                  `$${Math.round((values.loanAmount || 0) + result.unsecuredTotalInterestRaw).toLocaleString()}`,
                  `$${Math.round((values.loanAmount || 0) + result.securedTotalInterestRaw).toLocaleString()}`,
                ],
                deltas: [null, `-$${result.totalInterestSaving}`],
              },
            ]}
          />
          <OutputField
            highlight
            label="Having a guarantor will save you"
            value={`$${result.totalInterestSaving}`}
          />
          <OutputField
            label="Guarantor Usable Equity (80% LVR)"
            value={`$${result.usableEquity}`}
          />
        </CalculatorOutputs>
      </CalculatorLayout>
    </FormProvider>
  );
};

export default GuarantorLoan;
